import React from 'react';
import PropTypes from 'prop-types';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import { XMarkIcon, CreditCardIcon, CalendarDaysIcon, UserIcon } from '@heroicons/react/24/outline';

export default function SubscriptionManagementModal({ 
  isOpen, 
  onClose, 
  subscription,
  onCancelSubscription
}) {

  const formatSubscriptionDetails = () => {
    if (!subscription) {
      return {
        planName: 'Loading...',
        price: '$0.00/month',
        features: [],
        billingPeriod: 'Monthly',
        isMessaging: false
      };
    }
    
    const isMessaging = subscription.isMessaging || subscription.messagingPlanActive;
    const period = subscription.period || 'Month';
    const lines = subscription.lines || 1;
    
    return {
      planName: subscription.bundleName || `${lines} Line${lines > 1 ? 's' : ''}`,
      price: subscription.displayPrice || '$4.99/month',
      features: [
        `${lines} phone line${lines > 1 ? 's' : ''}`,
        isMessaging ? 'Text messaging included' : 'Voice calls & forwarding',
        'Email relay protection',
        'Privacy monitoring'
      ],
      billingPeriod: period === 'Annual' ? 'Yearly' : period === 'Quarterly' || period === 'Quaterly' ? 'Every 3 months' : 'Monthly',
      isMessaging
    };
  };

  const details = formatSubscriptionDetails();

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
      
      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel className="mx-auto max-w-2xl w-full">
            {/* Liquid Glass Panel */}
            <div 
              className="relative bg-black/20 backdrop-blur-2xl rounded-3xl border border-white/10 max-h-[85vh] flex flex-col"
              style={{
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 20px 40px rgba(0,0,0,0.3)'
              }}
            >
              {/* Close Button */}
              <button
                onClick={onClose}
                className="absolute top-6 right-6 z-10 w-10 h-10 rounded-full bg-white/10 backdrop-blur border border-white/20 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/20 transition-all"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>

              {/* Header - Fixed */}
              <div className="px-8 pt-8 pb-6 border-b border-white/10 flex-shrink-0">
                <div className="flex items-center gap-3 mb-2">
                  <CreditCardIcon className="h-8 w-8 text-indigo-400" />
                  <h2 className="text-3xl font-light text-white">Manage Subscription</h2>
                </div>
                <p className="text-white/60 text-lg">
                  View and manage your current subscription details.
                </p>
              </div>

              {/* Scrollable Content */}
              <div className="px-8 py-6 overflow-y-auto flex-1 min-h-0">
                {!subscription ? (
                  /* Loading State */
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-400 mx-auto mb-4"></div>
                    <p className="text-white/60">Loading subscription details...</p>
                  </div>
                ) : (
                  <>
                {/* Current Plan Card */}
                <div className="mb-8 p-6 rounded-xl bg-gradient-to-r from-indigo-500/20 to-purple-500/20 border border-indigo-400/30">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                    <span className="text-green-300 text-sm font-medium">Active Plan</span>
                  </div>
                  
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-white text-2xl font-light">{details.planName}</h3>
                      <p className="text-white/60 text-sm">
                        {details.billingPeriod} billing
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-white text-3xl font-light">{details.price}</p>
                    </div>
                  </div>

                  {/* Features List */}
                  <div className="space-y-2">
                    {details.features && details.features.length > 0 ? (
                      details.features.map((feature, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 bg-indigo-400 rounded-full"></div>
                          <span className="text-white/80 text-sm">{feature}</span>
                        </div>
                      ))
                    ) : (
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-gray-400 rounded-full"></div>
                        <span className="text-white/60 text-sm">Loading subscription details...</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Subscription Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                    <div className="flex items-center gap-3 mb-3">
                      <CalendarDaysIcon className="h-5 w-5 text-blue-400" />
                      <h4 className="text-white font-medium">Billing Cycle</h4>
                    </div>
                    <p className="text-white/80">{details.billingPeriod}</p>
                    <p className="text-white/60 text-sm mt-1">Next billing date will be displayed here</p>
                  </div>

                  <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                    <div className="flex items-center gap-3 mb-3">
                      <UserIcon className="h-5 w-5 text-green-400" />
                      <h4 className="text-white font-medium">Account Status</h4>
                    </div>
                    <p className="text-green-400 font-medium">Active</p>
                    <p className="text-white/60 text-sm mt-1">Your subscription is in good standing</p>
                  </div>
                </div>

                {/* Management Options */}
                <div className="space-y-4 mb-8">
                  <h3 className="text-white text-lg font-medium">Management Options</h3>
                  
                  <div className="p-4 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-all duration-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-white font-medium">Update Payment Method</h4>
                        <p className="text-white/60 text-sm">Change your default payment method</p>
                      </div>
                      <button className="px-4 py-2 bg-indigo-500/30 border border-indigo-400/30 rounded-lg text-white text-sm hover:bg-indigo-500/40 transition-all duration-200">
                        Update
                      </button>
                    </div>
                  </div>

                  <div className="p-4 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-all duration-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-white font-medium">Billing History</h4>
                        <p className="text-white/60 text-sm">View your past invoices and payments</p>
                      </div>
                      <button className="px-4 py-2 bg-indigo-500/30 border border-indigo-400/30 rounded-lg text-white text-sm hover:bg-indigo-500/40 transition-all duration-200">
                        View
                      </button>
                    </div>
                  </div>

                  <div className="p-4 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-all duration-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-white font-medium">Change Plan</h4>
                        <p className="text-white/60 text-sm">Upgrade or modify your current plan</p>
                      </div>
                      <button className="px-4 py-2 bg-indigo-500/30 border border-indigo-400/30 rounded-lg text-white text-sm hover:bg-indigo-500/40 transition-all duration-200">
                        Change
                      </button>
                    </div>
                  </div>
                </div>
                  </>
                )}
              </div>

              {/* Fixed Footer with Discrete Cancel CTA */}
              {subscription && (
                <div className="px-8 py-6 border-t border-white/10 flex-shrink-0">
                  <div className="flex justify-center">
                    <button
                      onClick={() => {
                        onClose();
                        if (onCancelSubscription) {
                          onCancelSubscription();
                        }
                      }}
                      className="text-white/40 hover:text-white/60 text-sm transition-all duration-200 underline-offset-4 hover:underline"
                    >
                      Cancel your subscription
                    </button>
                  </div>
                </div>
              )}
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}

SubscriptionManagementModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  subscription: PropTypes.object,
  onCancelSubscription: PropTypes.func
}; 